"use client"
/* */
import { getAsyncResource } from '@mittwald/react-use-promise';
import { useEffect } from 'react';
import { times } from 'remeda';
import { IncrementalLoaderState } from './IncrementalLoaderState.mjs';
import { hash } from 'object-code';

const emptyData = [];
class IncrementalLoader {
  list;
  dataSource;
  manualSorting;
  manualFiltering;
  manualPagination;
  loaderState;
  staticDataProperties = [];
  constructor(list, shape = {}) {
    const { source } = shape;
    this.dataSource = source ?? { staticData: emptyData };
    const manualPagination = "manualPagination" in this.dataSource ? this.dataSource.manualPagination : void 0;
    const manualFiltering = "manualFiltering" in this.dataSource ? this.dataSource.manualFiltering : void 0;
    const manualSorting = "manualSorting" in this.dataSource ? this.dataSource.manualSorting : void 0;
    this.list = list;
    this.loaderState = IncrementalLoaderState.useNew();
    this.manualPagination = manualPagination ?? false;
    this.manualFiltering = manualFiltering ?? this.manualPagination;
    this.manualSorting = manualSorting ?? this.manualPagination;
    this.list.filters.forEach((f) => f.onFilterUpdated(() => this.reset()));
    this.list.search?.onUpdated(() => this.reset());
    this.initStaticDataProperties();
  }
  static useNew(list, shape = {}) {
    return new IncrementalLoader(list, shape);
  }
  initStaticDataProperties() {
    const addPropertiesOfDataEntry = (data) => {
      if (typeof data !== "object" || data === null) {
        return;
      }
      Object.keys(data).filter((p) => !this.staticDataProperties.includes(p)).forEach((p) => {
        this.staticDataProperties.push(p);
      });
    };
    if ("staticData" in this.dataSource) {
      this.dataSource.staticData.slice(0, 100).forEach(addPropertiesOfDataEntry);
    }
  }
  reset() {
    this.loaderState.reset();
  }
  useIsLoading() {
    return this.loaderState.useIsLoading();
  }
  useIsInitiallyLoading() {
    return this.useIsLoading() && this.list.batches.getBatchIndex() === 0;
  }
  useData() {
    return this.loaderState.useMergedData();
  }
  getLoaderInvocationHooks() {
    const batchesCount = times(this.list.batches.getBatchIndex() + 1, (i) => i);
    return batchesCount.map((i) => () => {
      this.useLoadBatch(i);
    });
  }
  useLoadBatch(batchIndex) {
    const asyncResource = this.getBatchDataAsyncResource(batchIndex);
    asyncResource.use({
      useSuspense: false
    });
    this.useObserveBatchData(asyncResource, batchIndex);
    this.useObserveBatchLoadingState(asyncResource, batchIndex);
  }
  useObserveBatchLoadingState(asyncResource, batchIndex) {
    const setNewState = (newState) => {
      this.loaderState.setBatchLoadingState(
        batchIndex,
        this.loaderState.isBatchLoaded(batchIndex) ? "loaded" : newState
      );
    };
    useEffect(() => {
      setNewState(asyncResource.state.value);
      return asyncResource.state.observe(setNewState);
    }, [asyncResource, batchIndex]);
  }
  useObserveBatchData(asyncResource, batchIndex) {
    const setData = (loaderResult) => {
      const { data, itemTotalCount } = loaderResult;
      this.loaderState.setDataBatch(batchIndex, data);
      if (itemTotalCount !== void 0) {
        this.list.batches.updateItemTotalCount(itemTotalCount);
      }
    };
    useEffect(() => {
      if (asyncResource.value.value.isSet) {
        setData(asyncResource.value.value.value);
      }
      return asyncResource.value.observe((asyncData) => {
        if (asyncData.isSet) {
          setData(asyncData.value);
        }
      });
    }, [asyncResource, batchIndex]);
  }
  getDataLoaderOptions(batchIndex) {
    return {
      pagination: this.manualPagination ? {
        limit: this.list.batches.batchSize,
        offset: this.list.batches.batchSize * batchIndex
      } : void 0,
      sorting: this.manualSorting ? Object.fromEntries(
        this.list.sorting.filter((s) => s.isSorted()).map((s) => [s.property, s.direction])
      ) : void 0,
      filtering: this.manualFiltering ? Object.fromEntries(
        this.list.filters.filter((f) => f.getValue() !== null).map((f) => [
          f.property,
          {
            mode: f.mode,
            values: f.getArrayValue().map((v) => v.value)
          }
        ])
      ) : void 0,
      searchString: this.manualFiltering ? this.list.reactTable.searchString : void 0
    };
  }
  getBatchDataAsyncResource(batchIndex) {
    const dataSource = this.dataSource;
    const loaderOptions = this.getDataLoaderOptions(batchIndex);
    if ("staticData" in dataSource) {
      return getAsyncResource(
        async (staticData) => ({
          data: staticData,
          itemTotalCount: staticData.length
        }),
        [dataSource.staticData]
      );
    }
    if ("asyncLoader" in dataSource) {
      const asyncLoader = dataSource.asyncLoader;
      const dependencies = dataSource.dependencies;
      const loaderId = dependencies ? hash(dependencies).toString() : void 0;
      return getAsyncResource(asyncLoader, [loaderOptions], {
        loaderId
      });
    }
    if ("asyncResourceFactory" in dataSource) {
      const asyncResourceFactory = dataSource.asyncResourceFactory;
      return asyncResourceFactory(loaderOptions);
    }
    throw new Error("Unknown data source");
  }
}

export { IncrementalLoader };
//# sourceMappingURL=IncrementalLoader.mjs.map
